import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { apiClient } from '../api';
import { Plus, Trash2, Zap, List, PlayCircle, X } from 'lucide-react';
import GenerationAlert from '../components/GenerationAlert';
import { useGenerationAlert } from '../hooks/useGenerationAlert';

const Batch = () => {
    const [mode, setMode] = useState<'static' | 'dynamic'>('static');
    const [aspectRatio, setAspectRatio] = useState('portrait');
    const [batchName, setBatchName] = useState('');

    // Static Mode State
    const [staticPrompts, setStaticPrompts] = useState<string[]>(['']);

    // Dynamic Mode State
    const [template, setTemplate] = useState('');
    const [placeholders, setPlaceholders] = useState<string[]>([]);
    const [dynamicRows, setDynamicRows] = useState<Record<string, string>[]>([]);
    const [batchInputMode, setBatchInputMode] = useState(false);

    // Processing State
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ total: 0, current: 0 });
    const [showProgressModal, setShowProgressModal] = useState(false);

    // Generation Alert
    const { alertState, showSuccessAlert, showErrorAlert, hideAlert, checkAuthorizationBeforeGeneration } = useGenerationAlert();

    // --- Static Handlers ---
    const addStaticRow = () => setStaticPrompts([...staticPrompts, '']);
    const removeStaticRow = (idx: number) => {
        const newPrompts = [...staticPrompts];
        newPrompts.splice(idx, 1);
        setStaticPrompts(newPrompts);
    };
    const updateStaticPrompt = (idx: number, val: string) => {
        const newPrompts = [...staticPrompts];
        newPrompts[idx] = val;
        setStaticPrompts(newPrompts);
    };

    // --- Dynamic Handlers ---
    useEffect(() => {
        // Extract placeholders like <<KEY>>
        const matches = template.match(/<<([^>>]+)>>/g);
        if (matches) {
            const keys = matches.map(m => m.slice(2, -2));
            // Deduplicate
            const uniqueKeys = [...new Set(keys)];
            setPlaceholders(uniqueKeys);

            // Initialize rows if empty or keys changed significantly (optional logic, keeping simple for now)
            if (dynamicRows.length === 0) {
                setDynamicRows([{}]);
            }
        } else {
            setPlaceholders([]);
        }
    }, [template]);

    const addDynamicRow = () => setDynamicRows([...dynamicRows, {}]);
    const removeDynamicRow = (idx: number) => {
        const newRows = [...dynamicRows];
        newRows.splice(idx, 1);
        setDynamicRows(newRows);
    };
    const updateDynamicCell = (rowIdx: number, key: string, val: string) => {
        const newRows = [...dynamicRows];
        newRows[rowIdx] = { ...newRows[rowIdx], [key]: val };
        setDynamicRows(newRows);
    };

    // --- Paste Handler for Batch Input ---
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, rowIdx: number, _key: string) => {
        if (!batchInputMode) return;

        const pastedData = e.clipboardData.getData('text');

        // Check if it looks like CSV/TSV (multiple lines or tabs)
        if (!pastedData.includes('\n') && !pastedData.includes('\t')) {
            // Single value, let default paste behavior work
            return;
        }

        e.preventDefault();

        try {
            const lines = pastedData.trim().split('\n');

            // Detect delimiter (tab or comma)
            const firstLine = lines[0];
            const delimiter = firstLine.includes('\t') ? '\t' : ',';

            // Parse CSV/TSV with basic quoted value support
            const parseCSVLine = (line: string): string[] => {
                const result: string[] = [];
                let current = '';
                let inQuotes = false;

                for (let i = 0; i < line.length; i++) {
                    const char = line[i];

                    if (char === '"') {
                        inQuotes = !inQuotes;
                    } else if (char === delimiter && !inQuotes) {
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += char;
                    }
                }
                result.push(current.trim());
                return result;
            };

            // Parse all lines
            const parsedRows: Record<string, string>[] = [];

            for (const line of lines) {
                if (!line.trim()) continue;

                const values = parseCSVLine(line).map(v => v.replace(/^"|"$/g, ''));
                const row: Record<string, string> = {};

                // Map values to placeholders in order
                placeholders.forEach((placeholder, idx) => {
                    row[placeholder] = values[idx] || '';
                });

                parsedRows.push(row);
            }

            if (parsedRows.length > 0) {
                // Replace from current row onwards
                const newRows = [...dynamicRows];
                newRows.splice(rowIdx, parsedRows.length, ...parsedRows);
                setDynamicRows(newRows);
            }
        } catch (err) {
            console.error('Failed to parse pasted data', err);
        }
    };

    // --- Submission ---
    const handleBatchGenerate = async () => {
        let promptsToGenerate: { prompt: string, variables?: Record<string, string> }[] = [];

        if (mode === 'static') {
            promptsToGenerate = staticPrompts
                .filter(p => p.trim() !== '')
                .map(p => ({ prompt: p }));
        } else {
            promptsToGenerate = dynamicRows.map(row => {
                let finalPrompt = template;
                let isValid = true;
                placeholders.forEach(key => {
                    if (!row[key]) isValid = false;
                    finalPrompt = finalPrompt.replace(new RegExp(`<<${key}>>`, 'g'), row[key] || '');
                });
                return isValid ? { prompt: finalPrompt, variables: row } : null;
            }).filter(Boolean) as any;
        }

        if (promptsToGenerate.length === 0) {
            showErrorAlert('Please add at least one valid prompt.');
            return;
        }

        // Check authorization before proceeding
        if (!checkAuthorizationBeforeGeneration()) {
            return; // Alert will be shown by the hook
        }

        // Generate batch name if not provided
        const finalBatchName = batchName.trim() || `Batch - ${new Date().toLocaleString()}`;

        setIsProcessing(true);
        setShowProgressModal(true);
        setProgress({ total: promptsToGenerate.length, current: 0 });

        for (const item of promptsToGenerate) {
            try {
                const data = await apiClient.generate(item.prompt, aspectRatio);
                await db.addGeneration({
                    id: data.id,
                    batchId: data.batchId,
                    batchName: finalBatchName,
                    prompt: item.prompt,
                    aspectRatio,
                    mode: 'batch',
                    createdAt: Date.now(),
                    status: 'queued',
                    template: mode === 'dynamic' ? template : undefined,
                    variables: item.variables
                });
            } catch (err) {
                console.error('Batch generation error', err);
            }

            setProgress(prev => ({ ...prev, current: prev.current + 1 }));
        }

        setIsProcessing(false);
        showSuccessAlert(`Batch generation completed! ${promptsToGenerate.length} videos submitted.`);
    };

    return (
        <div className="page-container">
            <header className="page-header">
                <h2>Batch Generation</h2>
                <p>Generate multiple videos at once using static lists or dynamic templates.</p>
            </header>

            <div className="content-card">
                <div className="tabs">
                    <button
                        className={`tab ${mode === 'static' ? 'active' : ''}`}
                        onClick={() => setMode('static')}
                    >
                        <List size={18} /> Static List
                    </button>
                    <button
                        className={`tab ${mode === 'dynamic' ? 'active' : ''}`}
                        onClick={() => setMode('dynamic')}
                    >
                        <Zap size={18} /> Dynamic Template
                    </button>
                </div>

                <div className="batch-content">
                    <div className="form-group">
                        <label>Batch Name (Optional)</label>
                        <input
                            type="text"
                            value={batchName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchName(e.target.value)}
                            placeholder="e.g., Product Demo Videos"
                            className="w-full"
                        />
                        <small style={{ color: '#888', fontSize: '0.85rem' }}>Leave empty for auto-generated name</small>
                    </div>

                    <div className="form-group">
                        <label>Aspect Ratio</label>
                        <select
                            value={aspectRatio}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAspectRatio(e.target.value)}
                            className="w-full"
                        >
                            <option value="landscape">Landscape (16:9)</option>
                            <option value="portrait">Portrait (9:16)</option>
                            <option value="square">Square (1:1)</option>
                        </select>
                    </div>

                    {mode === 'static' ? (
                        <div className="static-list">
                            {staticPrompts.map((prompt, idx) => (
                                <div key={idx} className="input-row">
                                    <input
                                        type="text"
                                        value={prompt}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateStaticPrompt(idx, e.target.value)}
                                        placeholder={`Prompt ${idx + 1}`}
                                    />
                                    <button onClick={() => removeStaticRow(idx)} className="btn-icon danger">
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button onClick={addStaticRow} className="btn-secondary full-width">
                                <Plus size={18} /> Add Prompt
                            </button>
                        </div>
                    ) : (
                        <div className="dynamic-builder">
                            <div className="form-group">
                                <label>Template (Use {'<<PLACEHOLDER>>'})</label>
                                <textarea
                                    value={template}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTemplate(e.target.value)}
                                    placeholder="E.g., A cinematic shot of a <<ANIMAL>> in <<LOCATION>>"
                                    rows={3}
                                />
                            </div>

                            {placeholders.length > 0 && (
                                <>
                                    <div className="form-group" style={{
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '8px',
                                        padding: '1rem',
                                        marginBottom: '1.5rem'
                                    }}>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}>
                                            <div style={{
                                                position: 'relative',
                                                width: '44px',
                                                height: '24px',
                                                background: batchInputMode ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                                                borderRadius: '12px',
                                                transition: 'background 0.2s ease',
                                                cursor: 'pointer'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={batchInputMode}
                                                    onChange={(e) => setBatchInputMode(e.target.checked)}
                                                    style={{
                                                        position: 'absolute',
                                                        opacity: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '2px',
                                                    left: batchInputMode ? '22px' : '2px',
                                                    width: '20px',
                                                    height: '20px',
                                                    background: 'white',
                                                    borderRadius: '50%',
                                                    transition: 'left 0.2s ease',
                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                                }}></div>
                                            </div>
                                            <div>
                                                <div style={{
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem',
                                                    color: '#fff',
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    Batch Input Mode
                                                </div>
                                                <div style={{
                                                    color: '#888',
                                                    fontSize: '0.85rem',
                                                    lineHeight: '1.4'
                                                }}>
                                                    {batchInputMode
                                                        ? '✓ Paste CSV/TSV data directly into cells'
                                                        : 'Enable to paste CSV or TSV data into table cells'
                                                    }
                                                </div>
                                            </div>
                                        </label>
                                    </div>

                                    <div className="dynamic-table-wrapper">
                                        <table className="dynamic-table">
                                            <thead>
                                                <tr>
                                                    {placeholders.map(key => <th key={key}>{key}</th>)}
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dynamicRows.map((row, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        {placeholders.map(key => (
                                                            <td key={key}>
                                                                <input
                                                                    type="text"
                                                                    value={row[key] || ''}
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDynamicCell(rowIdx, key, e.target.value)}
                                                                    onPaste={(e) => handlePaste(e, rowIdx, key)}
                                                                    placeholder={key}
                                                                />
                                                            </td>
                                                        ))}
                                                        <td>
                                                            <button onClick={() => removeDynamicRow(rowIdx)} className="btn-icon danger">
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <button onClick={addDynamicRow} className="btn-secondary full-width mt-4">
                                            <Plus size={18} /> Add Row
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="actions mt-6">
                        <button onClick={handleBatchGenerate} className="btn-primary full-width" disabled={isProcessing}>
                            <PlayCircle size={20} />
                            Start Batch Generation
                        </button>
                    </div>
                </div>
            </div>

            {showProgressModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Batch Progress</h3>
                            {!isProcessing && (
                                <button onClick={() => setShowProgressModal(false)} className="btn-icon">
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                        <div className="modal-body">
                            <div className="progress-bar-container">
                                <div
                                    className="progress-bar"
                                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                                ></div>
                            </div>
                            <p className="text-center mt-2">
                                {isProcessing ? 'Submitting requests...' : 'Batch submission complete!'}
                            </p>
                            <p className="text-center text-muted">
                                {progress.current} / {progress.total}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <GenerationAlert
                isVisible={alertState.isVisible}
                type={alertState.type}
                title={alertState.title}
                message={alertState.message}
                onClose={hideAlert}
            />
        </div>
    );
};

export default Batch;
